import * as React from "react";
import { parseISO, format } from "date-fns";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@racoon/ui";
import { FileUpload, ConnectProto } from "@racoon/api";
import { IcCancel } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { toDateLike } from "@racoon/lib";
import type { LocationPhoto } from "../model/location-photo";

type SliderPhoto = FileUpload | ConnectProto.FileUpload | LocationPhoto;

interface LocationPhotosSliderProps {
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: SliderPhoto[];
  isLoading?: boolean;
  initialSlide?: number;
}

export const LocationPhotosSlider: React.FC<LocationPhotosSliderProps> = ({
  title,
  open,
  onOpenChange,
  photos,
  isLoading = false,
  initialSlide = 0,
}) => {
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(initialSlide);
  const t = useT();

  React.useEffect(() => {
    if (open && carouselApi) {
      setCurrent(initialSlide);
      carouselApi.scrollTo(initialSlide, true);
    }
  }, [open, initialSlide, carouselApi]);

  React.useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setCurrent(carouselApi.selectedScrollSnap());

    carouselApi.on("select", () => {
      setCurrent(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const formatDate = (dateInput?: string | Date): { time: string; date: string } => {
    if (!dateInput) return { time: "", date: "" };
    try {
      const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
      return {
        time: format(date, "HH:mm"),
        date: format(date, "dd.MM.yy"),
      };
    } catch {
      return { time: "", date: "" };
    }
  };

  const formatDriverName = (photo: SliderPhoto): string => {
    const named = photo as Partial<LocationPhoto> & {
      driver_first_name?: string;
      driver_last_name?: string;
    };
    const firstName = named.driverFirstName ?? named.driver_first_name ?? "";
    const lastName = named.driverLastName ?? named.driver_last_name ?? "";
    return `${firstName} ${lastName}`.trim() || t(Namespace.PickupLocation, "driverNotSpecified");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-[60rem] w-[60rem] !p-4 gap-4 bg-white rounded-2xl overflow-auto"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between w-full">
          <DialogTitle className="text-base font-semibold text-text-primary leading-4 tracking-[-0.16px]">
            {title ?? ""}
          </DialogTitle>
          <button
            onClick={() => onOpenChange(false)}
            className="cursor-pointer flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
          >
            <IcCancel className="w-8 h-8 text-text-primary" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center max-w-[672px] h-[640px]">
            <p className="text-text-secondary">{t(Namespace.Common, "loading")}</p>
          </div>
        ) : photos.length === 0 ? (
          <div className="flex items-center justify-center max-w-[672px] h-[640px]">
            <p className="text-text-secondary">{t(Namespace.PickupLocation, "locationPhotos")}</p>
          </div>
        ) : (
          <div className="relative w-full rounded-lg overflow-hidden">
            <Carousel
              setApi={setCarouselApi}
              className="w-full h-full"
              opts={{ startIndex: initialSlide }}
            >
              <CarouselContent className="h-full">
                {photos.map((photo) => {
                  const dateInfo = formatDate(
                    toDateLike((photo as any).created_at ?? (photo as any).createdAt) ?? undefined,
                  );
                  return (
                    <CarouselItem key={String(photo.id)} className="relative h-full pl-0">
                      <div className="relative w-full h-full flex items-center justify-center bg-black rounded-lg overflow-hidden">
                        <div
                          className="max-w-full max-h-full aspect-[4/3] w-full rounded-lg bg-contain bg-center bg-no-repeat"
                          style={{ backgroundImage: `url("${photo.url}")` }}
                        />

                        <div className="absolute top-2 left-2 right-2 flex justify-between items-start z-10 pointer-events-none">
                          <div className="bg-[#303338] flex items-center justify-center gap-4 h-8 px-2 py-2 rounded-lg">
                            <p className="text-sm text-[#F2F4F7] leading-[14px] tracking-[-0.14px]">
                              {dateInfo.time}
                            </p>
                            <p className="text-sm text-[#F2F4F7] leading-[14px] tracking-[-0.14px]">
                              {dateInfo.date}
                            </p>
                          </div>
                          {/* Full driver name on the right */}
                          <div className="bg-[#303338] flex items-center justify-center h-8 px-[10px] py-2 rounded-lg">
                            <p className="text-sm text-[#F2F4F7] leading-[14px] tracking-[-0.14px]">
                              {formatDriverName(photo)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>

              <div className="absolute bottom-[50%] left-2 right-2 flex justify-between items-center z-10 pointer-events-none">
                <CarouselPrevious
                  className="relative left-0 top-0 translate-y-0 bg-white border border-border-primary w-6 h-6 p-2 rounded-lg pointer-events-auto hover:bg-muted"
                  variant="outline"
                  size="icon"
                />
                <CarouselNext
                  className="relative right-0 top-0 translate-y-0 bg-white border border-border-primary w-6 h-6 p-2 rounded-lg pointer-events-auto hover:bg-muted"
                  variant="outline"
                  size="icon"
                />
              </div>

              {photos.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center justify-center gap-4 z-10">
                  {photos.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === current ? "bg-[#F2F4F7]" : "bg-[#1D1F23]"
                      }`}
                    />
                  ))}
                </div>
              )}
            </Carousel>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
